import {
  Box,
  Button,
  ColumnDatas,
  DropdownBody,
  DropdownItem,
  Icons,
  Table,
  Tabs,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import useAdminSettingStore from "../../redux/dispatcher/useAdminSettingStore";
import { AssignmentDto, TypeDto } from "../../types/adminSetting";
import utilsCommon from "../../shared/utils/common";
import useCheckboxGroup from "fds/src/hooks/useCheckboxGroup";
import InsertOrUpdateTypeModal from "./InsertOrUpdateTypeModal";
import InsertOrUpdateAssignmentModal from "./InsertOrUpdateAssignmentModal";
import { nanoid } from "@reduxjs/toolkit";

const AdminSetting: React.FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [height, setHeight] = useState(window.innerHeight - 260);
  const checkboxGroup = useCheckboxGroup<string>();
  const toast = useToast();
  const tableRef = useRef<HTMLDivElement>(null);

  const [typeList, setTypeList] = useState([] as TypeDto[]);
  const [assignmentList, setAssignmentList] = useState([] as AssignmentDto[]);

  const [targetType, setTargetType] = useState({} as TypeDto);
  const [targetAssignment, setTargetAssignment] = useState({} as AssignmentDto);

  const [typeMenu, setTypeMenu] = useState<{
    x: number;
    y: number;
    target: TypeDto;
  }>({
    x: 0,
    y: 0,
    target: {} as TypeDto,
  });
  const [assignmentMenu, setAssignmentMenu] = useState<{
    x: number;
    y: number;
    target: AssignmentDto;
  }>({
    x: 0,
    y: 0,
    target: {} as AssignmentDto,
  });
  const inputRef = useRef<HTMLTableRowElement>(null);

  const [showInsertOrUpdateType, toggleInsertOrUpdateType] = useState(false);
  const [showInsertOrUpdateAssignment, toggleInsertOrUpdateAssignment] =
    useState(false);

  const {
    adminSettingState,
    getManageType,
    getAssingment,
    deleteManageType,
    deleteAssignment,
  } = useAdminSettingStore();

  useEffect(() => {
    if (tab === 0) {
      getManageType();
    } else if (tab === 1) {
      getAssingment();
    }
  }, [tab]);

  useEffect(() => {
    setTypeList(adminSettingState.manageTypeList);
  }, [adminSettingState.manageTypeList]);

  useEffect(() => {
    setAssignmentList(adminSettingState.assignmentList);
  }, [adminSettingState.assignmentList]);

  useEffect(() => {
    if (!showInsertOrUpdateType) {
      setTargetType({} as TypeDto);
    }
  }, [showInsertOrUpdateType]);

  useEffect(() => {
    if (!showInsertOrUpdateAssignment) {
      setTargetAssignment({} as AssignmentDto);
    }
  }, [showInsertOrUpdateAssignment]);

  useEffect(() => {
    const handleResize = () => {
      if (tableRef.current) {
        const rect = tableRef.current.getBoundingClientRect();
        setHeight(window.innerHeight - rect.top - 72);
      }
    };

    const element = tableRef.current;

    const resizeObserver = new ResizeObserver(() => {
      if (element) {
        const rect = element.getBoundingClientRect();
        setHeight(window.innerHeight - rect.top - 72);
      }
    });

    if (element) {
      resizeObserver.observe(element);
    }
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (element) {
        resizeObserver.unobserve(element);
      }
    };
  }, []);

  const handleTurnOffSettng = (): void => {
    setTypeMenu({
      x: 0,
      y: 0,
      target: {} as TypeDto,
    });
    setAssignmentMenu({
      x: 0,
      y: 0,
      target: {} as AssignmentDto,
    });
  };

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (inputRef.current && !(inputRef.current! as any).contains(e.target)) {
      handleTurnOffSettng();
      document.removeEventListener("click", handleClickOutside, true);
    }
  }, []);
  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  });

  /** deleteTypes : 유형을 삭제합니다. */
  const deleteTypeFunc = async (types: TypeDto[]) => {
    let typeName = types?.map((item, index) => item.atName && item.atName);
    let typeCode = types
      ?.map((item) => item.atCode)
      .filter((item): item is string => item !== undefined);
    const confirmDeleteTypes = window.confirm(
      typeName.length === 1
        ? t("admin.adminSetting.deleteType", { name: typeName[0] })
        : t("admin.adminSetting.deleteTypeMulti", {
            name: typeName[0],
            count: typeName.length - 1,
          })
    );
    if (confirmDeleteTypes && typeCode) {
      const resp: any = await deleteManageType(typeCode);
      if (resp.error) {
        switch (resp.error.name) {
          case "USING_AT_CODE":
            toast.toastMsg(
              nanoid(),
              t("admin.adminSetting.noDeleteType"),
              "error"
            );
            break;
          default:
            toast.toastMsg(nanoid(), t(resp.error.message), "error");
            break;
        }
      } else {
        toast.toastMsg(nanoid(), t("admin.common.alert.delete"), "success");
      }
      checkboxGroup.handleAllClick([]);
    }
  };

  /** deleteAssignment : 관리자를 삭제합니다. */
  const deleteAssignmentFunc = async (assignments: AssignmentDto[]) => {
    let assignmentName = assignments?.map((item, index) => item.adminId);
    const confirmDeleteTypes = window.confirm(
      assignmentName.length === 1
        ? t("admin.adminSetting.deleteAdmin", {
            name: utilsCommon.getNameWithId(
              assignments[0].userInfo?.name
                ? assignments[0].userInfo?.name
                : "",
              assignments[0].userInfo?.id ? assignments[0].userInfo?.id : ""
            ),
          })
        : t("admin.adminSetting.deleteAdminMulti", {
            name: utilsCommon.getNameWithId(
              assignments[0].userInfo?.name
                ? assignments[0].userInfo?.name
                : "",
              assignments[0].userInfo?.id ? assignments[0].userInfo?.id : ""
            ),
            count: assignmentName.length - 1,
          })
    );
    if (confirmDeleteTypes) {
      const result: any = await deleteAssignment(assignmentName);
      if (result.error) {
        switch (result.error.name) {
          case "USING_AT_CODE":
            toast.toastMsg(
              nanoid(),
              t("admin.adminSetting.noDeleteType"),
              "error"
            );
            break;
          default:
            toast.toastMsg(nanoid(), t(result.error.message), "error");
            break;
        }
      } else {
        toast.toastMsg(nanoid(), t("admin.common.alert.delete"), "success");
      }
      checkboxGroup.handleAllClick([]);
    }
  };

  const renderTypeMenuButton = (target: any) => {
    return (
      <DropdownBody
        dropdownRef={inputRef}
        width={190}
        location={{ x: target.x, y: target.y }}
      >
        <DropdownItem
          label={t("admin.common.button.modify")}
          onClick={() => {
            setTargetType(target.target);
            toggleInsertOrUpdateType(true);
            handleTurnOffSettng();
          }}
        />
        <DropdownItem
          label={t("admin.common.button.delete")}
          onClick={() => {
            deleteTypeFunc([target.target]);
            handleTurnOffSettng();
          }}
        />
      </DropdownBody>
    );
  };

  const renderAssignmentMenuButton = (target: any) => {
    return (
      <DropdownBody
        dropdownRef={inputRef}
        width={190}
        location={{ x: target.x, y: target.y }}
      >
        <DropdownItem
          label={t("admin.common.button.modify")}
          onClick={() => {
            setTargetAssignment(target.target);
            toggleInsertOrUpdateAssignment(true);
            handleTurnOffSettng();
          }}
        />
        <DropdownItem
          label={t("admin.common.button.delete")}
          onClick={() => {
            deleteAssignmentFunc([target.target]);
            handleTurnOffSettng();
          }}
        />
      </DropdownBody>
    );
  };

  function transformTypeList(data: any[] | undefined) {
    const dataTransformed = [] as ColumnDatas[];
    data &&
      data?.map((item: any, index: number) => {
        const dataItemTransformed = {
          data: [
            {
              columnText: item.atName,
            },
            {
              columnText: item.atDesc,
            },
            {
              columnText: (
                <Box>
                  {item.atMenuScope
                    .filter((item: string) => item !== "eco_storage")
                    .map((item: string, index: number) =>
                      index === 0
                        ? utilsCommon.convertRightMenu(item)
                        : "," + utilsCommon.convertRightMenu(item)
                    )}
                </Box>
              ),
            },
          ],
          id: item.atCode,
          checkBoxClick: () => {
            checkboxGroup.handleClick(item.atCode);
          },
          hover: (
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setTypeMenu({
                  x: e.pageX,
                  y: e.pageY,
                  target: item,
                });
              }}
              btnStyle="icon"
              type="primary"
              size="sm"
              leadingIcon={<Icons variant="line" label="moreVertical" />}
            />
          ),
        };
        dataTransformed.push(dataItemTransformed);
      });
    return dataTransformed;
  }

  function transformAssignmentList(data: any[] | undefined) {
    const dataTransformed = [] as ColumnDatas[];
    data &&
      data?.map((item: any, index: number) => {
        const dataItemTransformed = {
          data: [
            {
              columnText: item.userInfo
                ? utilsCommon.getNameWithId(item.userInfo.name, item.adminId)
                : utilsCommon.getNameWithId("", item.adminId),
            },
            {
              columnText: item.atName,
            },
            {
              columnText: (
                <Box>
                  {item.allowIpList.map((item: any, index: number) => {
                    if (item.ipFrom === item.ipTo)
                      return (
                        <Typography key={index}>{item.ipFromStr}</Typography>
                      );
                    else
                      return (
                        <Typography key={index}>
                          {item.ipFromStr + " ~ " + item.ipToStr}
                        </Typography>
                      );
                  })}
                </Box>
              ),
            },
            {
              columnText:
                item.multiFactor !== "NONE"
                  ? t("admin.common.label.allow")
                  : t("admin.common.label.disallow"),
            },
          ],
          id: item.adminId,
          checkBoxClick: () => {
            checkboxGroup.handleClick(item.adminId);
          },
          hover: (
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setAssignmentMenu({
                  x: e.pageX,
                  y: e.pageY,
                  target: item,
                });
              }}
              btnStyle="icon"
              type="primary"
              size="sm"
              leadingIcon={<Icons variant="line" label="moreVertical" />}
            />
          ),
        };
        dataTransformed.push(dataItemTransformed);
      });
    return dataTransformed;
  }

  return (
    <Box classes="adminSetting" direction="column">
      <Box classes="adminSetting-title" justifyContent="space-between">
        <Typography type="h1">{t("admin.menu.adminSetting")}</Typography>
        {tab === 0 ? (
          <Box classes="separateBox">
            {checkboxGroup.selected.length > 0 && (
              <Button
                type="secondary"
                btnStyle="fill"
                onClick={() => {
                  deleteTypeFunc(
                    typeList.filter((item) =>
                      checkboxGroup.selected.includes(item.atCode + "")
                    )
                  );
                  checkboxGroup.handleAllClick([]);
                }}
                label={t("admin.common.button.delete")}
              />
            )}
            <Button
              btnStyle="fill"
              onClick={() => toggleInsertOrUpdateType(true)}
              label={t("admin.common.button.add")}
              classes="mg ml-8"
            />
          </Box>
        ) : (
          <Box classes="separateBox">
            {checkboxGroup.selected.length > 0 && (
              <Button
                type="secondary"
                btnStyle="fill"
                onClick={() => {
                  deleteAssignmentFunc(
                    assignmentList.filter((item) =>
                      checkboxGroup.selected.includes(item.adminId + "")
                    )
                  );
                  checkboxGroup.handleAllClick([]);
                }}
                label={t("admin.common.button.delete")}
              />
            )}
            <Button
              btnStyle="fill"
              onClick={() => toggleInsertOrUpdateAssignment(true)}
              label={t("admin.common.button.add")}
              classes="mg ml-8"
            />
          </Box>
        )}
      </Box>
      <Tabs
        tabs={[
          {
            color: "default",
            isNew: false,
            text: t("admin.common.label.right"),
          },
          {
            color: "default",
            isNew: false,
            text: t("admin.common.label.register"),
          },
        ]}
        selectTab={(value) => {
          setTab(value);
        }}
        selected={tab}
        size={"md"}
        type={"leftFull"}
      />
      <Box ref={tableRef} classes="mg mt-12">
        {tab === 0 ? (
          <Table
            width={"100%"}
            height={height + "px"}
            header={[
              {
                headerText: t("admin.adminSetting.type"),
                width: "200px",
                order: "",
                orderAction: () => {},
              },
              {
                headerText: t("admin.adminSetting.desc"),
                width: "200px",
                order: "",
                orderAction: () => {},
              },
              {
                headerText: t("admin.adminSetting.menu"),
                width: "calc(100% - 450px)",
                order: "",
                orderAction: () => {},
              },
            ]}
            data={transformTypeList(typeList)}
            totalCount={typeList ? typeList.length : 0}
            clickIds={checkboxGroup.selected.map((index) => String(index))}
            checkBoxClick={() =>
              checkboxGroup.handleAllClick(
                checkboxGroup.selected.length === typeList.length
                  ? []
                  : typeList.map((q) => q.atCode + "")
              )
            }
            endReached={() => {}}
            columnHeight="md"
          />
        ) : (
          <Table
            width={"100%"}
            height={height + "px"}
            header={[
              {
                headerText: t("admin.adminSetting.userNameId"),
                width: "300px",
                order: "",
                orderAction: () => {},
              },
              {
                headerText: t("admin.adminSetting.type"),
                width: "200px",
                order: "",
                orderAction: () => {},
              },
              {
                headerText: t("admin.adminSetting.ip"),
                width: "calc(100% - 800px)",
                order: "",
                orderAction: () => {},
              },
              {
                headerText: t("admin.adminSetting.otpLogin"),
                width: "200px",
                order: "",
                orderAction: () => {},
              },
            ]}
            data={transformAssignmentList(assignmentList)}
            totalCount={assignmentList ? assignmentList.length : 0}
            clickIds={checkboxGroup.selected.map((index) => String(index))}
            checkBoxClick={() =>
              checkboxGroup.handleAllClick(
                checkboxGroup.selected.length === assignmentList.length
                  ? []
                  : assignmentList.map((q) => q.adminId + "")
              )
            }
            endReached={() => {}}
            columnHeight="md"
          />
        )}
      </Box>
      {typeMenu.x > 0 && typeMenu.y > 0 && renderTypeMenuButton(typeMenu)}
      {assignmentMenu.x > 0 &&
        assignmentMenu.y > 0 &&
        renderAssignmentMenuButton(assignmentMenu)}
      {showInsertOrUpdateType && (
        <InsertOrUpdateTypeModal
          showInsertOrUpdateType={showInsertOrUpdateType}
          toggleInsertOrUpdateType={toggleInsertOrUpdateType}
          targetType={targetType}
        />
      )}
      {showInsertOrUpdateAssignment && (
        <InsertOrUpdateAssignmentModal
          showInsertOrUpdateAssignment={showInsertOrUpdateAssignment}
          toggleInsertOrUpdateAssignment={toggleInsertOrUpdateAssignment}
          typeList={typeList}
          targetAssignment={targetAssignment}
        />
      )}
    </Box>
  );
};

export default AdminSetting;
